import { useState } from 'react'
import { Button, useFeedback } from '@/components/ui'
import { cn } from '@/lib/cn'
import { subscribeToNewsletter } from '@/lib/newsletter'

/**
 * Email signup for the Fugue Lab mailing list. Posts to the app's public
 * subscribe endpoint (contact kernel + welcome sequence). Honeypot-guarded.
 */
export function NewsletterSignup({ className }: { className?: string }) {
  const { toast } = useFeedback()
  const [email, setEmail] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'done'>('idle')

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (honeypot) return // bot
    if (status === 'loading') return

    setStatus('loading')
    try {
      const result = await subscribeToNewsletter({ email })
      setStatus('done')
      toast(result.alreadySubscribed ? "You're already on the list." : "You're in. Check your inbox.", 'success')
    } catch (err) {
      setStatus('idle')
      toast(err instanceof Error ? err.message : 'Something went wrong. Please try again.', 'error')
    }
  }

  if (status === 'done') {
    return (
      <p className={cn('text-muted-foreground text-sm', className)}>
        Thanks for subscribing. There's a welcome note on its way.
      </p>
    )
  }

  return (
    <form onSubmit={onSubmit} className={cn('space-y-2', className)}>
      <label htmlFor="newsletter-email" className="text-muted text-xs tracking-wider uppercase">
        Get occasional notes from Garrett
      </label>
      <div className="flex gap-2">
        <input
          id="newsletter-email"
          type="email"
          name="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          className="border-border bg-background text-foreground placeholder:text-muted focus:ring-ring w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
        />
        <Button type="submit" disabled={status === 'loading'}>
          {status === 'loading' ? 'Joining…' : 'Subscribe'}
        </Button>
      </div>
      {/* Honeypot: hidden from users, catches bots */}
      <input
        type="text"
        name="company"
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="hidden"
        aria-hidden="true"
      />
    </form>
  )
}
